import {useRef} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {Wand2} from 'lucide-react';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';
import {getProduct} from '@client/pages/public/enroll/enrollModel';
import type {CoverageTier, ProductCategory, ProductConfig} from '@client/pages/public/enroll/types';
import type {CheckoutValues} from '@client/pages/public/enroll/checkout/checkoutSchema';

// Dev-only test fixtures. Shared applicant, varied dependents so we can pair each with an upstream
// tier to exercise the coverage checks (family + none, spouse-tier + child, employee-only + deps, …).
const DEV_BASE: Omit<CheckoutValues, 'dependents'> = {
  firstName: 'Jordan',
  lastName: 'Reyes',
  dob: '1990-05-15',
  gender: 'F',
  dateOfHire: '2020-03-02',
  ssn: '123-45-6789',
  email: 'jordan.reyes@example.com',
  phone: '555-123-4567',
  address1: '123 Maple Street',
  address2: 'Apt 4B',
  city: 'Austin',
  state: 'TX',
  zip: '73301',
  beneficiary: {
    firstName: 'Taylor',
    lastName: 'Reyes',
    relationship: 'spouse',
    dob: '1991-08-22',
    phone: '555-987-6543',
    address: '123 Maple Street',
    city: 'Austin',
    state: 'TX',
    zip: '73301',
  },
  smsConsent: true,
};

type DevDependent = CheckoutValues['dependents'][number];

const DEV_DEPENDENTS = {
  spouse: {firstName: 'Taylor', lastName: 'Reyes', relationship: 'spouse', dob: '1991-08-22', gender: 'M', ssn: '987-65-4321'},
  child: {firstName: 'Riley', lastName: 'Reyes', relationship: 'child', dob: '2016-03-10', gender: 'F', ssn: '111-22-3333'},
  child2: {firstName: 'Sam', lastName: 'Reyes', relationship: 'child', dob: '2018-07-01', gender: 'M', ssn: '222-33-4444'},
  domesticPartner: {firstName: 'Alex', lastName: 'Reyes', relationship: 'domestic-partner', dob: '1990-02-02', gender: 'M', ssn: '333-44-5555'},
  other: {firstName: 'Jamie', lastName: 'Reyes', relationship: 'other', dob: '2000-01-01', gender: 'F', ssn: '444-55-6666'},
} satisfies Record<string, DevDependent>;

const DEV_SCENARIOS: {label: string; dependents: DevDependent[]}[] = [
  {label: 'No dependents', dependents: []},
  {label: 'Spouse + child', dependents: [DEV_DEPENDENTS.spouse, DEV_DEPENDENTS.child]},
  {label: 'Spouse only', dependents: [DEV_DEPENDENTS.spouse]},
  {label: 'Child only', dependents: [DEV_DEPENDENTS.child]},
  {label: 'Two children', dependents: [DEV_DEPENDENTS.child, DEV_DEPENDENTS.child2]},
  {label: 'Domestic partner', dependents: [DEV_DEPENDENTS.domesticPartner]},
  {label: 'Other dependent', dependents: [DEV_DEPENDENTS.other]},
];

const DEV_TIER_STEPS: ProductCategory[] = ['medical', 'dental', 'vision'];

const DEV_TIER_OPTIONS: {value: string; label: string}[] = [
  {value: 'decline', label: 'Decline'},
  {value: 'employee', label: 'Employee'},
  {value: 'employeeSpouse', label: '+ Spouse'},
  {value: 'employeeChildren', label: '+ Children'},
  {value: 'family', label: 'Family'},
];

/**
 * Dev-only checkout controls: per-product tier toggles + one-click dependent scenarios, so the
 * coverage checks can be exercised without hand-filling the form. Gate the call site with
 * `import.meta.env.DEV` so it drops from production builds.
 */
export function CheckoutDevTools({form}: {form: UseFormReturn<CheckoutValues>}) {
  const {isDemo} = useEnrollPreview();
  return (
    <div
      className={`absolute right-0 -top-8 z-10 rounded-xl px-3.5 py-2.5 shadow-sm ${
        isDemo ? 'border border-amber-300 bg-amber-50' : 'border border-neutral-200 bg-white'
      }`}
    >
      <div className='mb-2 flex items-center gap-1.5 text-xs font-semibold text-neutral-700'>
        <Wand2 className='h-3.5 w-3.5 text-[var(--enroll-accent)]' />
        Fill shortcuts
        {isDemo && (
          <span className='rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-900'>
            Demo only
          </span>
        )}
      </div>
      <div className='flex items-start gap-5'>
        <div>
          <p className='mb-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-400'>
            Change plan selections
          </p>
          <div className='flex gap-1.5'>
            <DevTierControls />
          </div>
        </div>
        <div>
          <p className='mb-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-400'>
            Autofill client values
          </p>
          <DevTestData onFill={(dependents) => form.reset({...DEV_BASE, dependents})} />
        </div>
      </div>
      <p className={`mt-2 text-[10px] leading-snug ${isDemo ? 'text-amber-700' : 'text-neutral-400'}`}>
        {isDemo
          ? 'Demo mode only — these never appear on a real member enrollment site.'
          : 'Preview only — never on a published site.'}
      </p>
    </div>
  );
}

function DevTierControls() {
  const {group, selections, getTier, setTier, selectPlan, declineProduct} = useEnrollSteps();
  const products = DEV_TIER_STEPS.map((category) => getProduct(group, category)).filter(
    (product): product is ProductConfig => !!product && product.tierSelectable !== false,
  );
  if (products.length === 0) return null;

  function handleChange(category: ProductCategory, value: string) {
    if (value === 'decline') {
      declineProduct(category);
      return;
    }
    // Ensure the category is elected (first plan) so the tier actually takes effect for the checks.
    const firstPlan = group.plans.find((plan) => plan.category === category);
    if (firstPlan) selectPlan(category, firstPlan.id);
    setTier(category, value as CoverageTier);
  }

  return (
    <>
      {products.map((product) => {
        const selection = selections[product.category];
        const value = selection?.declined
          ? 'decline'
          : selection?.planId
            ? getTier(product.category)
            : '';
        return (
          <label
            key={product.category}
            className='flex items-center gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-2 py-1 text-xs font-medium text-neutral-600'
          >
            <span className='uppercase'>{product.category.slice(0, 3)}</span>
            <select
              value={value}
              onChange={(event) => handleChange(product.category, event.target.value)}
              className='bg-transparent text-xs text-neutral-700 outline-none'
            >
              <option value='' disabled>
                —
              </option>
              {DEV_TIER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        );
      })}
    </>
  );
}

function DevTestData({onFill}: {onFill: (dependents: DevDependent[]) => void}) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  return (
    <details ref={detailsRef} className='relative'>
      <summary className='inline-flex cursor-pointer list-none items-center gap-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-100'>
        Choose a sample ▾
      </summary>
      <div className='absolute right-0 z-10 mt-1 flex w-48 flex-col gap-0.5 rounded-lg border border-neutral-200 bg-white p-1.5 shadow-lg'>
        {DEV_SCENARIOS.map((scenario) => (
          <button
            key={scenario.label}
            type='button'
            onClick={() => {
              onFill(scenario.dependents);
              if (detailsRef.current) detailsRef.current.open = false;
            }}
            className='rounded px-2 py-1 text-left text-xs text-neutral-700 transition-colors hover:bg-neutral-100'
          >
            {scenario.label}
          </button>
        ))}
      </div>
    </details>
  );
}
